import { Fade } from "@mui/material";
import { NO_OP_CALLBACK } from "@smartrr/shared/constants";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { getNextActualDeliveryForCpsGroup } from "@smartrr/shared/utils/getScheduledDeliveries";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import React, { useCallback, useEffect, useState } from "react";

import { useCloseModal, useUpdateModalProps } from "@customer-app/state/actionCreators/modal";
import { useSmartrrCustomerSelector } from "@customer-app/state/typedCustomerReduxHooks";

import { GiftView } from "./components/GiftView";
import { SkipView } from "./components/SkipView";
import { UpcomingOrderCard } from "./components/UpcomingOrderCard";
import { ValidViews } from "./OrderOptionsModalInner";

import { getGift } from "../../../../utils";
import { usePurchasablesForAdding } from "../../../hooks/usePurchasablesForAdding";
import { DeliveryAccess } from "@customer-app/state/stores/delivery";
import { getDeliveryId } from "@customer-app/state/stores/delivery/DeliveryId";

export interface IUpcomingOrdersModalProps {}

export const UpcomingOrdersModal = ({}: IUpcomingOrdersModalProps): JSX.Element => {
  const closeModal = useCloseModal();

  const scheduledDeliveries = DeliveryAccess.useSortedDeliveries();
  const upcomingDelivery = DeliveryAccess.useUpcomingDelivery();

  const purchasablesForAdding = usePurchasablesForAdding(upcomingDelivery);
  const updateModal = useUpdateModalProps();

  const localizeShorthand = "subscriptions.yourSubscriptions.upcomingOrdersModal";
  const organization = useSmartrrCustomerSelector(state => state.auth.organization);
  const [view, setView] = useState<{
    view: ValidViews;
    delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState> | undefined;
  }>({ view: "NullView", delivery: undefined });

  useEffect(() => {
    // Resetting view once modal is closed then reopened
    setView({ view: "NullView", delivery: undefined });
  }, []);

  const onCancel = useCallback(() => {
    setView({ view: "NullView", delivery: undefined });
  }, []);

  const switchToOrderOptions = useCallback(
    (delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>, isSellingPlanDeleted: boolean) => {
      const { purchaseState, indexFromScheduleStart } = delivery!;

      const gift = getGift(purchaseState, indexFromScheduleStart)!;

      const delayDateAvailable =
        delivery?.date.toDateString() ===
        getNextActualDeliveryForCpsGroup(organization!, [purchaseState])?.[0].toDateString();

      updateModal({
        componentOverride: "ORDER_OPTIONS",
        propOverrides: {
          deliveryId: getDeliveryId(delivery),
          delayDateAvailable,
          gift,
          isSellingPlanDeleted,
          purchasablesForAdding,
          showUpcomingOrderButton: true,
          organization: organization!,
        },
      });
    },
    [organization, purchasablesForAdding]
  );

  const ViewComponent = () => {
    const delivery = view?.delivery;

    switch (view.view) {
      case "Skip": {
        return (
          <Fade in={view.view === "Skip"}>
            <div>
              <SkipView setIconButtonInactive={onCancel} delivery={delivery!} />
            </div>
          </Fade>
        );
      }
      case "Gift": {
        return (
          <Fade in={view.view === "Gift"}>
            <div>
              <GiftView delivery={delivery!} onCancel={onCancel} onConfirm={NO_OP_CALLBACK} />
            </div>
          </Fade>
        );
      }
      default: {
        // Shouldn't hit here
        return <div></div>;
      }
    }
  };

  return (
    <React.Fragment>
      {view.view === "NullView" ? (
        <React.Fragment>
          {scheduledDeliveries.map(delivery => (
            <UpcomingOrderCard
              key={delivery.date.toDateString()}
              delivery={delivery}
              imageSize="small"
              setView={setView}
              switchToOrderOptions={switchToOrderOptions}
            />
          ))}
          <AquariusButton filled onClick={() => closeModal()}>
            {typedLocalize("general.backToAccountBtn")}
          </AquariusButton>
        </React.Fragment>
      ) : (
        <React.Fragment>
          <ViewComponent />
          {view.view === "Default" ? (
            <AquariusButton onClick={() => setView({ view: "NullView", delivery: undefined })}>
              {localize(`${localizeShorthand}.backToOrders`)}
            </AquariusButton>
          ) : null}
        </React.Fragment>
      )}
    </React.Fragment>
  );
};
